import IActivityLogEntry from "../interface/iActivityLogEntry";
import formatAppointment, { APPOINTMENT_AREAS, formatDate } from "./formatAppointment";

export interface IDetailRow {
  label: string;
  value: string;
}

const FIELD_LABELS: Record<string, string> = {
  gender: "Anrede",
  firstName: "Vorname",
  vorname: "Vorname",
  lastName: "Nachname",
  nachname: "Nachname",
  email: "E-Mail",
  phoneNumber: "Telefon",
  qualification: "Qualifizierung",
  location: "Standort",
  locationId: "Standort",
  startOfQualification: "Beginn Qualifizierung",
  endOfQualification: "Ende Qualifizierung",
  teams_link: "Teams-Link",
  type: "Termin-Art",
  date: "Datum",
  fromTime: "Von",
  tillTime: "Bis",
  ip: "IP-Adresse",
  browser: "Browser",
  fileName: "Datei",
  reason: "Grund",
  rebookedFrom: "Umgebucht von (TN-Nr.)",
  rebookedTo: "Umgebucht auf (TN-Nr.)",
  cancelledAppointments: "Stornierte Termine",
  coachName: "Coach",
  active: "Aktiv",
  kind: "Art",
  uploaderId: "Hochgeladen von (Nr.)",
  from: "Vorher",
  to: "Nachher",
};

const KIND_LABELS: Record<string, string> = {
  appointment: "Terminerinnerung",
  anonymization: "Erinnerung Anonymisierung",
};

/** Shown elsewhere or meaningless to an admin. */
const HIDDEN_DETAILS = ["migratedFrom", "appointment", "payload", "changes", "realName", "path"];
const HIDDEN_PAYLOAD = ["repeatEmail", "sessionId", "participantId", "coachId"];
/** Payload fields already covered by the appointment line. */
const APPOINTMENT_PAYLOAD = ["date", "fromTime", "tillTime", "type"];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Notes legacy entries wrote on success; they only repeat the action. Every other
 * message stays visible, also on successful entries (e.g. "appointment already exists").
 */
const TECHNICAL_SUCCESS_NOTES = [
  /by admin \(user_id( \d+)?\)$/,
  /^appointment created: \d+$/,
  /^appointment and booking created$/,
  /^(coach|participant) upload successful$/,
];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Payload keys come from requests; "__proto__" or "constructor" must not hit the prototype. */
const labelOf = (key: string): string =>
  Object.prototype.hasOwnProperty.call(FIELD_LABELS, key) ? FIELD_LABELS[key] : key;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Readable text for a value, or null when there is nothing worth showing. */
const formatValue = (key: string, value: unknown): string | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (isRecord(value)) {
    // select fields are stored as { label, value }
    return typeof value.label === "string" ? value.label : null;
  }
  if (typeof value === "object") return null;

  const text = String(value);
  if (key === "kind") {
    return Object.prototype.hasOwnProperty.call(KIND_LABELS, text) ? KIND_LABELS[text] : text;
  }
  if (ISO_DATE.test(text)) return formatDate(text);
  if (UUID.test(text)) return null;
  return text;
};

const formatChange = (key: string, change: unknown): string | null => {
  if (!isRecord(change)) return null;
  if (
    (typeof change.from === "string" && UUID.test(change.from)) ||
    (typeof change.to === "string" && UUID.test(change.to))
  ) {
    return "geändert";
  }
  return `${formatValue(key, change.from) ?? "–"} → ${formatValue(key, change.to) ?? "–"}`;
};

const referenceLabel = (area: string): string => {
  if (APPOINTMENT_AREAS.includes(area)) return "Termin-Nr.";
  if (area === "file") return "Datei-Nr.";
  if (area === "coach") return "Coach-Nr.";
  return "Referenz";
};

/**
 * Turns an entry's message, appointment and raw details into labelled rows: payloads
 * and changes are flattened, internal ids and empty values dropped.
 */
const describeActivityLogDetails = (entry: IActivityLogEntry): IDetailRow[] => {
  const rows: IDetailRow[] = [];
  const add = (label: string, value: string | null): void => {
    const isDuplicate = rows.some((row) => row.label === label && row.value === value);
    if (value !== null && !isDuplicate) rows.push({ label, value });
  };

  add("Termin", formatAppointment(entry));
  const isTechnicalNote =
    entry.status === "success" &&
    TECHNICAL_SUCCESS_NOTES.some((note) => note.test(entry.message));
  add("Meldung", entry.message === "" || isTechnicalNote ? null : entry.message);
  add(referenceLabel(entry.area), entry.subjectRef);

  Object.entries(entry.details)
    .filter(([key]) => !HIDDEN_DETAILS.includes(key))
    .forEach(([key, value]) => add(labelOf(key), formatValue(key, value)));

  const { payload, changes } = entry.details;
  if (isRecord(payload)) {
    Object.entries(payload)
      .filter(
        ([key]) =>
          !HIDDEN_PAYLOAD.includes(key) &&
          !(entry.appointment !== null && APPOINTMENT_PAYLOAD.includes(key))
      )
      .forEach(([key, value]) => add(labelOf(key), formatValue(key, value)));
  }
  if (isRecord(changes)) {
    Object.entries(changes).forEach(([key, change]) =>
      add(labelOf(key), formatChange(key, change))
    );
  }

  return rows;
};

export default describeActivityLogDetails;
